import { db, type StorageEntry } from '../db';
import { calculateExpiry } from '../utils/expiry';
import { formatDate } from '../utils/time';

function toPlain<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

export async function addToStorage(
  childId: string,
  params: {
    sessionId?: string;
    location: 'fridge' | 'freezer' | 'deep-freezer';
    volumeMl: number;
    storedAt?: number;
  },
): Promise<StorageEntry> {
  const now = Date.now();
  const storedAt = params.storedAt || now;
  const entry: StorageEntry = {
    id: crypto.randomUUID(),
    childId,
    sessionId: params.sessionId,
    location: params.location,
    originalVolumeMl: params.volumeMl,
    remainingVolumeMl: params.volumeMl,
    storedAt,
    expiresAt: calculateExpiry(storedAt, params.location),
    label: `${formatDate(storedAt)} — ${params.volumeMl}mL — ${params.location}`,
    createdAt: now,
    updatedAt: now,
  };
  await db.storageEntries.add(toPlain(entry));
  return entry;
}

export async function getActiveInventory(childId: string): Promise<StorageEntry[]> {
  const entries = await db.storageEntries
    .where('childId')
    .equals(childId)
    .sortBy('storedAt');
  return entries.filter((e) => !e.deletedAt && !e.depletedAt && e.remainingVolumeMl > 0);
}

export async function getInventoryByLocation(
  childId: string,
  location: string,
): Promise<StorageEntry[]> {
  return (await getActiveInventory(childId)).filter((e) => e.location === location);
}

export async function useFromStorage(id: string, volumeUsed: number): Promise<void> {
  const entry = await db.storageEntries.get(id);
  if (!entry) return;

  const remaining = Math.max(0, entry.remainingVolumeMl - volumeUsed);
  await db.storageEntries.update(id, {
    remainingVolumeMl: remaining,
    lastUsedAt: Date.now(),
    depletedAt: remaining === 0 ? Date.now() : undefined,
    updatedAt: Date.now(),
  });
}

export async function deleteStorageEntry(id: string): Promise<void> {
  await db.storageEntries.update(id, { deletedAt: Date.now(), updatedAt: Date.now() });
}

export async function getStorageSummary(
  childId: string,
): Promise<{ fridge: number; freezer: number; deepFreezer: number; total: number }> {
  const all = await getActiveInventory(childId);
  const fridge = all
    .filter((e) => e.location === 'fridge')
    .reduce((sum, e) => sum + e.remainingVolumeMl, 0);
  const freezer = all
    .filter((e) => e.location === 'freezer')
    .reduce((sum, e) => sum + e.remainingVolumeMl, 0);
  const deepFreezer = all
    .filter((e) => e.location === 'deep-freezer')
    .reduce((sum, e) => sum + e.remainingVolumeMl, 0);
  return { fridge, freezer, deepFreezer, total: fridge + freezer + deepFreezer };
}
